import { createContext, useContext } from "react";
import { Socket } from "socket.io-client";
import { DefaultEventsMap } from "@socket.io/component-emitter";
import { IClientDataResponse } from "../_services/interface/client.interface";
import { IResponse } from "../_services/interface/user.interface";
import { ICertificate } from "../_services/interface/certificate.interface";

/**
 * Contexts that hold the authenticated app's shared state. They are provided by
 * AppLayout (above all routes) and consumed by the Sidebar and the pages.
 *
 * ClientContext / NotificationContext / ResourcesContext used to live in
 * components/sidebar.tsx and are re-exported from there for backward
 * compatibility with the (~27) existing import sites.
 */

// --- ClientContext -----------------------------------------------------------

interface ClientContextType {
  guid_client: string | null;
  client_name: string | null;
}

export const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const useClientContext = () => {
  const context = useContext(ClientContext);
  if (context === undefined || context.guid_client === null) {
    throw new Error("useClientContext must be used within a ClientProvider");
  }
  return context;
};

// --- NotificationContext -----------------------------------------------------

export const NotificationContext = createContext({
  total: 0,
  unread: 0,
  important: 0,
  folders: {
    INBOX: 0,
    ARCHIVED: 0,
  },
  updateNotificationContext: (obj: any) => {},
});

// --- ResourcesContext --------------------------------------------------------

// GEN-4021: `resourcesLoaded` como no AppDataContext — quem filtra por jurisdição usa `loadedResources`.
export const ResourcesContext = createContext<any>({
  resources: [],
  resourcesLoaded: false,
  resourcesError: false,
});

// --- AppDataContext ----------------------------------------------------------

export interface IAppDataContext {
  validation: IResponse;
  socket: Socket<DefaultEventsMap, DefaultEventsMap> | undefined;
  onUpdate: React.Dispatch<React.SetStateAction<boolean>>;

  currentClient: IClientDataResponse | undefined;
  guid_client: string;
  collaborator: IClientDataResponse | undefined;
  selectClient: (guidClient: string) => void;

  resources: string[];
  /** GEN-4021: true só depois que `getResources` respondeu com sucesso para o cliente atual.
   *  Enquanto false, `resources` é `[]` por não saber — não por o cliente não ter recurso —, e
   *  as regras de jurisdição tratam como "nenhuma visível" (fail-closed). Use `loadedResources`. */
  resourcesLoaded: boolean;
  /** GEN-4021: `getResources` falhou para o cliente atual (a AppShell mostra o aviso). */
  resourcesError: boolean;

  certificates: ICertificate[];
  certificatesUse: ICertificate[];
  certificatesTaxCurrentAccount: ICertificate[];
  certificatesDte: ICertificate[];
  /** False until the certificate lists have loaded for the current client. The
   *  product-derived flags below are only meaningful once this is true. */
  certificatesLoaded: boolean;

  hasCpfProduct: boolean;
  hasCertificate: boolean;
  /** Cliente com alguma consulta da Conta Corrente (Situação Fiscal e/ou CADIN Federal — GEN-3984). */
  hasTaxCurrentAccountProduct: boolean;
}

export const AppDataContext = createContext<IAppDataContext | undefined>(undefined);

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (context === undefined) {
    throw new Error("useAppData must be used within an AppLayout");
  }
  return context;
};

// --- ConfigGuardContext ------------------------------------------------------

/**
 * Drives the "unsaved changes" guard for the ConfigPanel. The ConfigPanel (and
 * its content/ subcomponents) report changes through setHasChange and react to
 * submit/onClose; the Sidebar funnels every navigation through requestNavigation
 * so it can intercept clicks while there are pending changes.
 */
export interface IConfigGuardContext {
  hasChange: boolean;
  setHasChange: React.Dispatch<React.SetStateAction<boolean>>;
  submit: boolean;
  setSubmit: React.Dispatch<React.SetStateAction<boolean>>;
  submitError: boolean;
  setSubmitError: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: boolean;
  setOnClose: React.Dispatch<React.SetStateAction<boolean>>;
  requestNavigation: (path: string, onNavigate?: () => void) => void;
}

export const ConfigGuardContext = createContext<IConfigGuardContext | undefined>(undefined);

export const useConfigGuard = () => {
  const context = useContext(ConfigGuardContext);
  if (context === undefined) {
    throw new Error("useConfigGuard must be used within an AppLayout");
  }
  return context;
};
